import React, { useEffect, useMemo, useRef, useState } from "react";
import FlexiDialog from "../../FlexiComponents/FlexiDialog";
import FlexiForm, { Fields } from "../../FlexiComponents/FlexiForm";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { StyledChip } from "../../components/FormFields/AgreementSelectField";
import { Loader } from "../../FlexiComponents/FlexiLoader";
import Box from "@material-ui/core/Box";
import selectFieldErrorsFromResponse from "../../utils/selectFieldErrorsFromResponse";
import removeFieldName from "../../utils/removeFieldName";

const CustomerSection = ({ values, index, customer }) => {
  const contactFields = useMemo(
    () => [
      [
        {
          fieldType: "text",
          name: `customer.${index}.contact.name`,
          required: true,
          label: "Neve"
        }
      ],
      [
        {
          fieldType: "text",
          name: `customer.${index}.contact.telephone`,
          required: true,
          label: "Telefonszáma"
        }
      ],
      [
        {
          fieldType: "text",
          name: `customer.${index}.contact.electronicMail`,
          required: true,
          label: "E-mail címe"
        }
      ]
    ],
    [index]
  );

  return (
    <>
      <Typography
        variant="h6"
        style={{ paddingTop: "16px", paddingBottom: "32px" }}
      >
        KIVEL TARTOD A KAPCSOLATOT A VEVŐDNÉL?
      </Typography>

      <Fields config={contactFields} />

      <Typography
        variant="h6"
        style={{ paddingTop: "16px", paddingBottom: "32px" }}
      >
        SZÁMLA VISSZAIGAZOLÁSÁSRA JOGOSULT KAPCSOLATTARTÓ ADATAI
      </Typography>

      <Fields
        config={[
          [
            {
              fieldType: "checkbox",
              name: `customer.${index}.sameContactForInvoiceConfirmation`,
              label: "Megegyezik a kapcsolattartó adataival"
            }
          ],
          [
            {
              fieldType: "text",
              name: `customer.${index}.contactForInvoiceConfirmation.name`,
              label: "Neve",
              disabled:
                values?.customer?.[index]?.sameContactForInvoiceConfirmation
            }
          ],
          [
            {
              fieldType: "text",
              name: `customer.${index}.contactForInvoiceConfirmation.telephone`,
              label: "Telefonszáma",
              disabled:
                values?.customer?.[index]?.sameContactForInvoiceConfirmation
            }
          ],
          [
            {
              fieldType: "email",
              name: `customer.${index}.contactForInvoiceConfirmation.electronicMail`,
              label: "E-mail címe",
              disabled:
                values?.customer?.[index]?.sameContactForInvoiceConfirmation
            }
          ]
        ]}
      />
    </>
  );
};

const FormDialog = ({
  onClose,
  open,
  onSubmit,
  agreementClaim,
  index,
  initialValues,
  postLoading,
  width = "lg"
}) => {
  const [step, setStep] = useState(0);
  const formRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(0);
    }
  }, [open]);

  const handleFormSubmit = async values => {
    const result = await onSubmit(values);
    if (!result) {
      return undefined;
    }

    const supplierErrors = {};

    if (result.supplier) {
      Object.entries(result.supplier).forEach(([k, msg]) => {
        const cleanKey = k.replace(/^(supplier|factorDataSheet)\./, "");
        const raw =
          typeof msg === "string" ? msg : Array.isArray(msg) ? msg[0] : String(msg);
        supplierErrors[cleanKey] = removeFieldName(raw);
      });
    }

    const parsedErrors =
      selectFieldErrorsFromResponse(result) ||
      selectFieldErrorsFromResponse(result.errors) ||
      selectFieldErrorsFromResponse(result.response);

    if (parsedErrors) {
      Object.entries(parsedErrors).forEach(([backendField, message]) => {
        const cleanKey = backendField.replace(/^(supplier|factorDataSheet)\./, "");
        if (!supplierErrors[cleanKey]) {
          const raw =
            typeof message === "string"
              ? message
              : Array.isArray(message)
              ? message[0]
              : String(message);
          supplierErrors[cleanKey] = removeFieldName(raw);
        }
      });
    }

    if (Object.keys(supplierErrors).length > 0) {
      setStep(0);
      setTimeout(() => {
        const formApi = formRef.current;
        if (formApi && typeof formApi.blur === "function") {
          Object.keys(supplierErrors).forEach(field => {
            formApi.blur(`supplier.${field}`);
          });
        }
        const el = document.querySelector(".field-error");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);

      return {
        supplier: supplierErrors
      };
    }

    return undefined;
  };
  const customers = agreementClaim?.customers || [];
  const lastStep = customers.length + 1;

  return (
    <FlexiDialog
      open={open}
      width={width}
      title={
        step === 1 ? (
          "Köszönjük, a saját adataid megadásával már készen is vagy!"
        ) : step === 0 ? (
          <Typography variant="h5">
            Online faktoradatlap - {agreementClaim?.supplier?.party?.partyName}
          </Typography>
        ) : (
          <div style={{ display: "inline-flex" }}>
            <Typography variant="h5">
              Online faktoradatlap - {customers[step - 2]?.party?.partyName}
            </Typography>

            <StyledChip
              key={customers[step - 2]?.calculation?.currency}
              label={customers[step - 2]?.calculation?.currency}
            />
          </div>
        )
      }
      onClose={onClose}
    >
      {postLoading && (
        <Box p={2} style={{ minWidth: 300, minHeight: 180 }}>
          <Loader />
          <Typography align="center" variant="h5">
            Az oldal töltődik!
          </Typography>
          <Typography align="center" variant="body2">
            Kis türelmet kérünk!
          </Typography>
        </Box>
      )}

      {/* Keep FlexiForm mounted while loading so it doesn't lose its state. */}
      <Box style={{ display: postLoading ? "none" : "block" }}>
        {step === 1 && (
          <Typography variant="body2" style={{ paddingBottom: "16px" }}>
            A következő lap/lapok a vevődre/vevőidre vonatkoznak, kérlek a rájuk
            vonatkozó adatokkal töltsd ki a következő részt.
          </Typography>
        )}

        <FlexiForm
          id="FactorSheetForm"
          FinalFormFormProps={{
            onSubmit: handleFormSubmit,
            initialValues,
            keepDirtyOnReinitialize: true,
            destroyOnUnregister: false
          }}
          render={({ handleSubmit, values, form }) => {
            formRef.current = form;

            return step === 1 ? (
              <Grid container>
                <Grid item xs={12} style={{ display: "inline-flex" }}>
                  <Button
                    disabled={form.getState().hasValidationErrors}
                    style={{ marginRight: "8px" }}
                    onClick={() => setStep(step + 1)}
                  >
                    Mentés és tovább
                  </Button>

                  <Button variant="text" onClick={() => setStep(step - 1)}>
                    Vissza
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div style={{ display: step === 0 ? "block" : "none" }}>
                    <Fields
                      config={[
                        [
                          {
                            fieldType: "text",
                            name: "supplier.bankAccountHuf",
                            label: "Bankszámlaszám(HUF)*",
                            disabled: !customers.some(
                              customer =>
                                customer.calculation?.currency === "HUF"
                            )
                          }
                        ],
                        [
                          {
                            fieldType: "text",
                            name: "supplier.bankAccountEur",
                            label: "Bankszámlaszám(EUR)*",
                            disabled: !customers.some(
                              customer =>
                                customer.calculation?.currency === "EUR"
                            )
                          }
                        ],
                        [
                          {
                            fieldType: "text",
                            name: "supplier.bankAccountUsd",
                            label: "Bankszámlaszám(USD)*",
                            disabled: !customers.some(
                              customer =>
                                customer.calculation?.currency === "USD"
                            )
                          }
                        ]
                      ]}
                    />
                  </div>

                  {customers.map((customer, customerIndex) => (
                    <div
                      key={customer?.id || customerIndex}
                      style={{
                        display: step === customerIndex + 2 ? "block" : "none"
                      }}
                    >
                      <CustomerSection
                        customer={customer}
                        index={customerIndex}
                        values={values}
                      />
                    </div>
                  ))}
                </Grid>

                <Grid item xs={12} style={{ display: "inline-flex" }}>
                  {step !== lastStep && (
                    <Button
                      disabled={form.getState().hasValidationErrors}
                      onClick={() => setStep(currentStep => currentStep + 1)}
                    >
                      Mentés és tovább
                    </Button>
                  )}

                  {step !== lastStep && step !== 0 && (
                    <Button
                      variant="text"
                      onClick={() => setStep(currentStep => currentStep - 1)}
                    >
                      Vissza
                    </Button>
                  )}

                  {step === lastStep && (
                    <>
                      <Button
                        disabled={form.getState().hasValidationErrors}
                        style={{ marginRight: "8px" }}
                        type="submit"
                      >
                        Mentés
                      </Button>

                      <Button
                        variant="text"
                        onClick={() =>
                          setStep(currentStep => currentStep - 1)
                        }
                      >
                        Vissza
                      </Button>
                    </>
                  )}
                </Grid>
              </Grid>
            );
          }}
        />
      </Box>
    </FlexiDialog>
  );
};

export default FormDialog;
